import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { IconKeyboardArrowRightBlue } from "assets/svg";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { UTCToJapanTime } from "common/helper/time.helper";
import { NoteTypeEnum } from "common/enums/NoteType.enum";
import { message } from "antd";
import { useCommonContext } from "contexts/CommonContext";
import { formatProductQuantity, productPrice } from "common/utils";

type AdminNote = {
  id: string | number;
  created_at: string;
  content: string;
};

const CompanyManagementTransactionViewPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const { companyId, transactionCode } = useParams();
  const [sellerDetail, setSellerDetail] = useState<any>({});
  const [transactionDetail, setTransactionDetail] = useState<any>({});
  const [buyerDetail, setBuyerDetail] = useState<any>({});
  const [productDetail, setProductDetail] = useState<any>({});
  const [messageDetail, setMessageDetail] = useState<any[]>([]);
  const [adminNote, setAdminNote] = useState<string>("");
  const [noteList, setNoteList] = useState<AdminNote[]>([]);

  useEffect(() => {
    fetchTransactionDetail();
    setHeaderTitle(
      `取引詳細（取引ID：#${transactionDetail?.code || transactionCode}）`,
    );
  }, []);

  useEffect(() => {
    fetchNoteList();
  }, [transactionDetail.id]);

  const getFullAddress = (info: any) => {
    if (!info) return "";
    const parts = [
      info?.prefecture,
      info?.city,
      info?.address,
      info?.address_line,
      info?.building_room,
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "";
  };

  const getUserId = (info: any) =>
    info?.user_id || info?.id || info?.code || "#U00123";

  const formatDateTime = (dateString: string) => {
    const japanTime = UTCToJapanTime(dateString);
    return `${japanTime.year}/${String(japanTime.month).padStart(2, "0")}/${String(japanTime.day).padStart(2, "0")} ${String(japanTime.hour).padStart(2, "0")}:${String(japanTime.minute).padStart(2, "0")}`;
  };

  const getSenderInfo = (senderType: number, senderName: string) => {
    if (senderType === UserTypeEnum.COMPANY_BUYER) {
      return {
        label: "買い手",
        companyName: senderName,
        className:
          "border-[2px] border-primary rounded px-2 py-1 text-[12px] text-primary",
      };
    } else if (senderType === UserTypeEnum.COMPANY_SELLER) {
      return {
        label: "売り手",
        companyName: senderName,
        className: "bg-primary rounded px-2 py-1 text-[12px] text-white",
      };
    }
    return {
      label: "管理者",
      companyName: senderName || "システム管理者",
      className: "bg-gray-500 rounded px-2 py-1 text-[12px] text-white",
    };
  };

  const handleSaveNote = () => {
    if (!adminNote.trim()) return;

    axiosInstance
      .post(API_ENDPOINTS.NOTE_LIST, {
        object_id: transactionDetail.id,
        object_type: NoteTypeEnum.TRANSACTION,
        content: adminNote,
      })
      .then((res) => {
        if (res.status === 201) {
          setAdminNote("");
          fetchNoteList();
          message.success("保存に成功しました");
        }
      })
      .catch(() => {
        message.error("保存に失敗しました");
      });
  };

  const fetchNoteList = () => {
    if (!transactionDetail.id) return;
    axiosInstance
      .get(API_ENDPOINTS.NOTE_LIST, {
        params: {
          sort: "id:asc",
          object_id: transactionDetail.id,
          object_type: NoteTypeEnum.TRANSACTION,
        },
      })
      .then((res) => {
        setNoteList(res.data.data || []);
      });
  };

  const fetchTransactionDetail = () => {
    axiosInstance
      .get(API_ENDPOINTS.TRANSACTION_LIST + "/" + transactionCode)
      .then((response) => {
        const data = response.data.data;
        setSellerDetail(data.seller);
        setBuyerDetail(data.buyer);
        setProductDetail(data.product);
        setTransactionDetail(data);

        axiosInstance
          .get(API_ENDPOINTS.GET_CHAT_MESSAGE, {
            params: {
              product_id: data.product_id,
              buyer_id: data.buyer_id,
              seller_id: data.seller_id,
            },
          })
          .then((res) => {
            setMessageDetail(res.data?.data?.messages || []);
          });
      });
  };

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー管理",
            link: "/admin/company-management",
          },
          {
            label: `取引履歴`,
            link: `/admin/company-management/${companyId}/transactions`,
          },
          {
            label: `取引詳細（取引ID: #${transactionDetail?.code || transactionCode}）`,
            isCurrent: true,
          },
        ]}
      />
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                取引の基本情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              取引ID
            </div>
            <div className="flex-1 p-4">#T1234</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              出品ID
            </div>
            <div className="flex-1 p-4">#00123</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品名
            </div>
            <div className="flex-1 p-4">
              {productDetail.product_name || "廃プラスチック"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              カテゴリー
            </div>
            <div className="flex-1 p-4">
              {productDetail.category || "プラスチック類"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              数量
            </div>
            <div className="flex-1 p-4">
              {formatProductQuantity(productDetail)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              処理区分
            </div>
            <div className="flex-1 p-4">
              {productDetail.process_type || "リサイクル"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品の状態
            </div>
            <div className="flex-1 p-4">
              {productDetail.condition || "良好（未使用／未破損）"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品の説明
            </div>
            <div className="flex-1 p-4">
              {productDetail.description ||
                "少し臭気があります。形状にばらつきあり。"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ステータス
            </div>
            <div className="flex-1 p-4">
              {productDetail.status || "取引完了"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              配送料の負担
            </div>
            <div className="flex-1 p-4">
              {productDetail.shipping_fee_burden || "送料込み（売り手側負担）"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              発送までの日数
            </div>
            <div className="flex-1 p-4">
              {productDetail.shipping_days || "2～3日で発送"}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              価格
            </div>
            <div className="flex-1 p-4">{productPrice(productDetail)}</div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${companyId}/product/${productDetail.code}/view`}
          className="text-primary md:text-[14px]"
        >
          このユーザーの詳細を見る
          <IconKeyboardArrowRightBlue className="inline" />
        </Link>
      </div>

      {/* 売り手企業情報 */}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                売り手企業情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザーID
            </div>
            <div className="flex-1 p-4">{getUserId(sellerDetail)}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人名
            </div>
            <div className="flex-1 p-4">
              {sellerDetail?.company_name ||
                sellerDetail?.name ||
                "株式会社〇〇"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              担当者名
            </div>
            <div className="flex-1 p-4">
              {sellerDetail?.contact_name ||
                [
                  sellerDetail?.contact_last_name,
                  sellerDetail?.contact_first_name,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                "山田 太郎"}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              メールアドレス
            </div>
            <div className="flex-1 p-4">
              {sellerDetail?.email || "seller@example.com"}
            </div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${sellerDetail?.code}/view`}
          className="text-primary md:text-[14px]"
        >
          このユーザーの詳細を見る
          <IconKeyboardArrowRightBlue className="inline" />
        </Link>
      </div>

      {/* 買い手企業情報 */}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                買い手企業情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザーID
            </div>
            <div className="flex-1 p-4">{getUserId(buyerDetail)}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人名
            </div>
            <div className="flex-1 p-4">
              {buyerDetail?.company_name ||
                buyerDetail?.name ||
                "日都産業株式会社"}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              担当者名
            </div>
            <div className="flex-1 p-4">
              {buyerDetail?.contact_name ||
                [
                  buyerDetail?.contact_last_name,
                  buyerDetail?.contact_first_name,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                "佐藤 花子"}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              メールアドレス
            </div>
            <div className="flex-1 p-4">
              {buyerDetail?.email || "buyer@example.com"}
            </div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${buyerDetail?.code}/view`}
          className="text-primary md:text-[14px]"
        >
          このユーザーの詳細を見る
          <IconKeyboardArrowRightBlue className="inline" />
        </Link>
      </div>

      {/* 取引チャット・進捗履歴 */}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm"></div>
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                取引チャット・進捗履歴
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          {messageDetail.length > 0 ? (
            messageDetail.map((message, index) => {
              const senderInfo = getSenderInfo(
                message.sender_type,
                message.sender_name,
              );
              const isLastItem = index === messageDetail.length - 1;

              return (
                <div
                  key={message.id}
                  className={`flex ${!isLastItem ? "border-b border-b-neutral-border" : ""}`}
                >
                  <div className="p-4 font-bold border-r border-r-neutral-border max-w-[300px] w-[100%]">
                    <span className={`${senderInfo.className} mr-[10px]`}>
                      {senderInfo.label}
                    </span>
                    {senderInfo.companyName}
                  </div>
                  <div className="flex-1 justify-between flex p-4">
                    <span>{message.content}</span>
                    <span className="text-[12px] text-[#ADBBCC]">
                      {formatDateTime(message.send_at)}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex">
              <div className="p-4 text-center w-full text-gray-500">
                チャット履歴がありません
              </div>
            </div>
          )}
        </div>
      </div>

      {/*table*/}
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                管理者メモ
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          {noteList.length === 0 ? (
            <div className="flex justify-center items-center p-8 text-neutral-500">
              操作ログがありません
            </div>
          ) : (
            noteList.map((note: AdminNote, idx: number) => {
              const { year, month, day, hour, minute } = UTCToJapanTime(
                note.created_at,
              );
              return (
                <div
                  className={`flex ${idx !== noteList.length - 1 ? "border-b border-b-neutral-border" : ""}`}
                  key={note.id}
                >
                  <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                    {`${year}/${month}/${day} ${hour}:${minute}`}
                  </div>
                  <div className="flex-1 p-4">{note.content}</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 管理者メモ */}
      <div className="w-[100%]">
        <div className="text-[18px] md:text-[20px] font-bold leading-9 mb-[15px]">
          管理者メモを追加
        </div>
        <textarea
          className="w-full min-h-[120px] p-3 border border-neutral-border rounded-[6px] outline-none focus:ring-2 focus:ring-primary text-sm"
          placeholder="例：メール返信が3日間ないため注意フラグ／画像に他社ロゴが写っていたため非公開対応 etc."
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
        />

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSaveNote}
            disabled={!adminNote.trim()}
            className={`px-5 py-2 rounded-[6px] text-white ${adminNote.trim() ? "bg-primary hover:opacity-90" : "bg-neutral-300 cursor-not-allowed"}`}
          >
            保存する
          </button>
        </div>
      </div>

      <div className="flex gap-2.5">
        <button
          onClick={() => window.history.back()}
          className="min-h-[50px] w-[340px] bg-primary-light text-primary hover:bg-primary hover:text-white text-[16px] font-bold border-0 rounded-[6px]"
        >
          一覧へ戻る
        </button>
      </div>
    </div>
  );
};

export default CompanyManagementTransactionViewPage;
